/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback, useMemo } from 'react'
import {
  CloseOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { Modal, Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'

/**
 * Props for MarkAllAsReadModal component
 */
interface MarkAllAsReadModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  unreadCount: number
  isLoading: boolean
  error: string | null
}

/**
 * Mark All As Read confirmation modal
 * Displays confirmation dialog before marking all notifications as read
 */
export const MarkAllAsReadModal: React.FC<MarkAllAsReadModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  unreadCount,
  isLoading,
  error,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Info Icon */}
        <CheckCircleOutlined
          css={css`
            font-size: 48px;
            color: ${colors.PRIMARY};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Mark All as Read
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Mark{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                {unreadCount}
              </span>{' '}
              notification{unreadCount === 1 ? '' : 's'} as read?
            </Text>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.PRIMARY};
              border-radius: 8px;
              border: 2px solid ${colors.PRIMARY};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={(e) => {
              e.stopPropagation()
              if (!isLoading) {
                onConfirm()
              }
            }}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={PRIMARY_BUTTON_CONTENT.text}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading ? 'MARKING AS READ...' : 'MARK AS READ'}
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={(e) => {
              e.stopPropagation()
              handleClose()
            }}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              CANCEL
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Props for DeleteNotificationModal component
 */
interface DeleteNotificationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
  error: string | null
}

/**
 * Delete Notification confirmation modal
 * Displays confirmation dialog before deleting a notification
 */
export const DeleteNotificationModal: React.FC<
  DeleteNotificationModalProps
> = ({ isOpen, onClose, onConfirm, isLoading, error }) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.ERROR_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Delete Notification
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Are you sure you want to delete this notification?
            </Text>
          </div>
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.ERROR_200};
              border-radius: 8px;
              border: 2px solid ${colors.ERROR_200};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={(e) => {
              e.stopPropagation()
              if (!isLoading) {
                onConfirm()
              }
            }}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading ? 'DELETING...' : 'DELETE'}
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={(e) => {
              e.stopPropagation()
              handleClose()
            }}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              CANCEL
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}
